/* eslint-disable  @typescript-eslint/no-explicit-any */
import {Component, Event, type EventEmitter, h, Listen, Method, Prop, State, Watch} from '@stencil/core';
import {CoseRequestService, QueryContext} from '../../../../../utils/services/cose/cose-request.service';
import {FacetsV2Service} from '../../../../../utils/services/facets/facets-v2.service';
import {isMobile} from '../../../../../utils/media';
import type {FacetsData} from '../../facets-build-wrapper';
import {first, map, Subject, takeUntil} from 'rxjs';
import type {ResultResponseDto} from '../../../../../models/cose-api/response/result-response.dto';
import {facetsDataCreator} from '../../utils/facets-data-creator';
import type {FilterNode} from '../../filter-node';
import {FilterChangeContext} from '../../models/filter-change-context.enum';
import type {ChipData} from '../../../../design-system/mobile-filters/filters-header-menu/filters-chips/filters-chips';
import {MobileFilteringAreaType} from '../../../../design-system/mobile-filters/models/mobile-filtering-area-type.enum';
import {cloneObjectWithNoReference} from '../../../../../utils/helpers/clone-object-with-no-reference';
import {type FacetsShowChildrenType} from '../../../../strategies/types/facets-config-strategy-data.type';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';

@Component({
  tag: 'mobile-filters-wrapper',
  styleUrl: 'mobile-filters-wrapper.scss',
  shadow: true,
})
export class MobileFiltersWrapper {
  private unsubscriber$: Subject<any> = new Subject<any>();
  private readonly positionCardNumberToHideMF: number = 3;
  private readonly headerMenuHeight = 54;
  private initialHeaderDisplayCss: string;
  private mobileFiltersElement?: HTMLElement | any;
  private countResultsElement?: HTMLElement;
  private tabsNavigatianHeader?: HTMLElement;
  private autocorrectComponent?: HTMLElement;
  private scrollYOnQuery?: number;
  private cardPositionIntervalWatch?: any;
  private hiddenComponentsHeight?: number;
  private scrollPositionToHideMB?: number;

  @Prop({ mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly rtl: boolean;
  @Prop() readonly hiddenFacets: string[] = [];
  @Prop() readonly headerSelector?: string;
  @Prop( ) readonly dividerMobileFacets?: string[];
  @Prop() readonly flatFacets: boolean;
  @Prop({ attribute: 'coseRequestService' }) readonly coseRequestService?: CoseRequestService;
  @Prop({ attribute: 'facetsService'} ) readonly facetsService?: FacetsV2Service;
  @Prop({ attribute: 'total-hits' }) readonly totalHitsProp?: string;
  @Prop() readonly tabName: string;
  @Prop({ attribute: 'facets-data' }) readonly facetsDataProp!: string;
  @Prop() readonly desktopShowChildren: FacetsShowChildrenType = 'allways';
  @Prop() readonly showFiltersSearchInputLimit: number | null;
  @Prop() readonly headerAdditionalComponents: {element: (param: boolean) => HTMLElement, elementHeight: number};
  @Watch('facetsDataProp')
  onFacestDataChange(newValue) {
      if(this.facetsDataState && !this.facetsService.skipWatchOnCount) {
        this.facetsDataState = {...this.facetsDataState, ...JSON.parse(newValue), ...{totalHits: this.totalHitsProp}};
      }
  }

  @State() facetsDataState: FacetsData;
  @State() mobileView: boolean = isMobile();


  @Event({
    eventName:'filter:did:change',
    bubbles: true
  })
    onFilterChange: EventEmitter<{filterNode: FilterNode, context: FilterChangeContext}>;

  @Event({
    eventName:'search:reset',
    bubbles: true
  })
    reset: EventEmitter<void>;

  @Event({eventName: 'search:mobileFilteringButtonResultsClick', bubbles: true}) mobileFilteringButtonResultsClick: EventEmitter<any>;
  @Event({eventName: 'search:mobileFilteringClose', bubbles: true}) mobileFilteringClose: EventEmitter<any>;
  @Event({eventName: 'search:mobileFilteringOpen', bubbles: true }) mobileFilteringOpen: EventEmitter<any>;
  @Event({eventName: 'search:mobileFilteringResetButtonClick', bubbles: true}) mobileFilteringResetButtonClick: EventEmitter<any>;
  @Event({eventName: 'on:chip:remove:mobile', bubbles: true}) onChipRemoveMobile: EventEmitter<any>;

  @Listen('filter:did:change:mobile')
  handleFilterDidChangeMobile(event: CustomEvent) {
      this.facetsService.currentContext = FilterChangeContext.mobile;
      this.facetsDataState.facetLocalChipsState.push(event.detail);
      this.disableFiltersOnSelectChange();
      this.onFilterChange.emit({filterNode: event.detail, context: FilterChangeContext.mobile});
      this.facetsService.facetsMobileService.onFacetChange(
        event.detail,
        this.facetsDataState.facetsState,
        this.facetsDataState.facets,
        false,
        this.dividerMobileFacets,
        this.flatFacets
      );
      this.countResults();
  }

  private disableFiltersOnSelectChange() {
      this.mobileFiltersElement.querySelectorAll('filters-tree-node').forEach(el=> {
          el.classList.add('disable-on-request');
          el.querySelectorAll('filter-checkbox').forEach((chel) => {
              chel.shadowRoot.querySelector('se-checkbox').disabled = 'true';
          });
      });

      this.mobileFiltersElement.querySelectorAll('filters-single-facet-node').forEach(el=> {
          el.classList.add('disable-on-request');
      });

      const filterResetElement = this.mobileFiltersElement.querySelector('filters-reset');
      if(filterResetElement) {
        filterResetElement.classList.add('disable-on-request');
      }

  }

  private enableFiltersOnIncomingResponse() {
      this.mobileFiltersElement.querySelectorAll('filters-tree-node').forEach(el=> {
          el.classList.remove('disable-on-request');
          el.querySelectorAll('filter-checkbox').forEach((chel) => {
              chel.shadowRoot.querySelector('se-checkbox').disabled = 'false';
          });
      });

      this.mobileFiltersElement.querySelectorAll('filters-single-facet-node').forEach(el=> {
          el.classList.remove('disable-on-request');
      });

      const filterResetElement = this.mobileFiltersElement.querySelector('filters-reset');
      if(filterResetElement) {
        filterResetElement.classList.remove('disable-on-request');
      }
  }

  private handleClear = (e: CustomEvent) => {
      this.onClearAndClose({...e.detail, onlyCollapse: true});
  };

  @Listen('filters:did:change:mobile')
  handleFiltersDidChangeMobile(event: CustomEvent) {
      this.facetsService.currentContext = FilterChangeContext.mobile;
      event.detail.filterNodes.forEach((filterNode,i) => {
          this.onFilterChange.emit({filterNode: event.detail, context: FilterChangeContext.mobile});
          this.facetsService.facetsMobileService.onFacetChange(
            filterNode,
            this.facetsDataState.facetsState,
            this.facetsDataState.facets,
            false,
            this.dividerMobileFacets,
            this.flatFacets
          );
      });
      this.countResults();
  }

  @Listen('on:chip:remove')
  handleCloseChip(event: CustomEvent) {
      const eventDetail: ChipData = event.detail;
      const filteredNodes = this.facetsDataState.facetsChipsState.filter((chip: FilterNode) => eventDetail.id === chip.uniqueId);

      if(this.flatFacets) {
        const filteredLocalNodes = this.facetsDataState.facetLocalChipsState.filter((chip: FilterNode) => eventDetail.id === chip.uniqueId);
        if(filteredLocalNodes.length > 0) {
          filteredLocalNodes[0].selected = false;
          this.facetsService.facetsMobileService.addOrRemoveLocalChipOnState(filteredLocalNodes[0], false);
        }
      }

      if(filteredNodes && filteredNodes.length > 0) {
        this.onChipRemoveMobile.emit({...eventDetail, ...{filterCount: filteredNodes[0].count, clickedArea: MobileFilteringAreaType.Chips}});
      }
  }

  @Method()
  async clearAllViewChange() {
      this.onClearAllViewChange();
  }

  @Method()
  async setCollapse(bool: boolean) {
      this.mobileFiltersElement.setCollapse(bool);
  }

  @Method()
  async setFixedView(bool: boolean) {
      this.mobileFiltersElement.setFixedView(bool);
  }

  @Method()
  async eventEmitOnResetButtonClick(context = QueryContext.ignored) {
      this.mobileFilteringResetButtonClick.emit({
        tabName: this.tabName,
        siteSearchKeyword: this.coseRequestService.context.query,
        context
      });
  }

  @Method()
  async initCountResults() {
      this.countResults();
  }

  get headerElement(): HTMLElement {
      return (document.querySelector(this.headerSelector) as HTMLElement);
  }

  componentWillLoad(): void {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            return fallback;
        };
      }
      this.tabsNavigatianHeader = document.querySelector('uiaas-search').shadowRoot.querySelector('search-tabs-nav-header');
      this.autocorrectComponent = document.querySelector('uiaas-search').shadowRoot.querySelector('uiaas-search-results-autocorrect');
      this.countResultsElement = this.coseRequestService.context.el.shadowRoot.querySelector('uiaas-search-mobile-filters-count-results');
      this.initialHeaderDisplayCss = this.headerElement.style.display;
      this.facetsDataState = JSON.parse(this.facetsDataProp);
      this.hiddenComponentsHeight = this.autocorrectComponent.offsetHeight + this.tabsNavigatianHeader.offsetHeight;
      this.scrollPositionToHideMB = this.headerMenuHeight + this.hiddenComponentsHeight;
      this.onCountQuery();
  }

  disconnectedCallback() {
      this.unsubscriber$.next(null);
      this.unsubscriber$.complete();
      if(this.cardPositionIntervalWatch) {
        clearInterval(this.cardPositionIntervalWatch);
      }
  }

  render() {
      return (
        (this.facetsDataState) ?
          <uiaas-search-mobile-filters
            ref={(e) => {this.mobileFiltersElement = e;}}
            facetsDataProp={JSON.stringify(this.facetsDataState)}
            isFlatFacets={this.flatFacets}
            hiddenFacets={JSON.stringify(this.hiddenFacets) as string}
            headerSelector={this.headerSelector}
            translationsProp={this.prepareTranslationsObject()}
            totalHitsProp={this.totalHitsProp}
            dividerMobileFacets={this.dividerMobileFacets}
            hiddenComponentsHeight={this.hiddenComponentsHeight.toString()}
            labelProvider={this.labelProvider as labelProviderInterface}
            rtl={this.rtl}
            scrollPositionForHide={this.scrollPositionToHideMB.toString()}
            onShowHeader={ () => this.onShowHeader() }
            onHideHeader={ () => this.onHideHeader() }
            onCollapsedChange={(e) => this.onCollapsedChange(e.detail)}
            onQuery={() => this.onQuery()}
            onClear={this.handleClear}
            onEscape={() => this.onEscape()}
            onReset:filters={() => this.onReset()}
            showFiltersSearchInputLimit={this.showFiltersSearchInputLimit}
            headerAdditionalComponents={this.headerAdditionalComponents}
          ></uiaas-search-mobile-filters>
          : ''
      );
  }

  private countResults(): void {
      this.facetsService.skipWatchOnCount = true;
      this.coseRequestService.initEscapePipe();
  }

  private prepareTranslationsObject(): string {
      return JSON.stringify({
        'filter': this.labelProvider('mobile-filters.filter', 'Filter'),
        'reset': this.labelProvider('se/products.facet.facets.reset', 'Reset all'),
        'show-results': this.labelProvider('mobile-filters.show-results', 'Show results'),
        'search-filter': this.labelProvider('filters.input.placeholder', 'Search in filter values')
      });
  }

  private onCollapsedChange(action: boolean) {
      if(!action) {
        this.bodyScrollEnable();
        if(this.cardPositionIntervalWatch) {
          clearInterval(this.cardPositionIntervalWatch);
        }
      } else {
        this.mobileFiltersElement.classList.remove('expand-width');
        this.bodyScrollDisable();
        this.mobileFilteringOpen.emit({
          tabName: this.tabName,
          siteSearchKeyword: this.coseRequestService.context.query
        });
      }
  }

  private onShowHeader(): void {
      this.showHideHeader(true);
      this.showHideResultCount(false);
      this.mobileFiltersElement.setCanHide(false);
      this.mobileFiltersElement.classList.add('was-expanded');
      if(this.facetsDataState.facetsState.length > 0) {
        this.mobileFiltersElement.classList.add('expand-width');
      }
      if(this.cardPositionIntervalWatch) {
        clearInterval(this.cardPositionIntervalWatch);
      }
  }

  private onHideHeader(): void {
      this.showHideHeader(false);
      this.showHideResultCount(true);
      this.mobileFiltersElement.setCanHide(false);
      this.mobileFiltersElement.classList.remove('was-expanded');
      this.mobileFiltersElement.classList.remove('expand-width');
      this.observeProductsCardClientPosition();
  }

  private onQuery(): void {
      this.facetsService.currentContext = FilterChangeContext.all;
      this.facetsDataState = cloneObjectWithNoReference({...this.facetsDataState, ...{facetsChipsState: cloneObjectWithNoReference(this.facetsDataState.previewsStateData.facetsChipsState)}});

      this.facetsService.skipWatchOnCount = false;
      this.facetsService.updateStatesFromMobile();
      this.mobileFilteringButtonResultsClick.emit({
        tabName: this.tabName,
        siteSearchKeyword: this.coseRequestService.context.query,
        context: QueryContext.mobileFilteringShowResults
      });

      this.bodyScrollEnable();

      if(this.cardPositionIntervalWatch) {
        clearInterval(this.cardPositionIntervalWatch);
      }

      this.observeProductsCardClientPosition();
      this.mobileFiltersElement.classList.remove('was-expanded');
      this.mobileFiltersElement.classList.remove('expand-width');
      this.scrollYOnQuery = (this.tabsNavigatianHeader.offsetHeight + this.autocorrectComponent.offsetHeight) + (this.headerMenuHeight - this.headerAdditionalComponents.elementHeight) + 13;
      this.showHideHeader();
      this.showHideResultCount(true);
      window.scrollTo(0, this.scrollYOnQuery);

      this.coseRequestService.initPipe(this.dividerMobileFacets)
        .pipe(
          takeUntil(this.unsubscriber$),
          first()
        )
        .subscribe(v => {
            this.coseRequestService.setOnQueryContext(QueryContext.mobileFilteringShowResults);
        });
  }

  private onCountQuery(): void {
      this.coseRequestService.currentResponse
        .pipe(
          takeUntil(this.unsubscriber$),
          map((response: ResultResponseDto<any>) => {
              const facets = response.facetsOptions.facets;
              const totalHits = response.stats.totalHits;
              return {facets, totalHits};
          }),
          map(( {facets, totalHits}) => {
              return facetsDataCreator(
                facets,
                [],
                this.dividerMobileFacets,
                this.facetsService.facetsMobileService.facetChipsState$.value,
                this.facetsService.facetsMobileService.facetsState$.value,
                this.facetsService.facetsMobileService.facetLocalChipsState$.value ,
                totalHits,
                this.desktopShowChildren,
                this.flatFacets,
                this.facetsService.currentContext === FilterChangeContext.resetAll ? this.facetsService.currentContext : FilterChangeContext.mobile);
          }
          ),
        )
        .subscribe(({
          totalHits,
          facets,
          filteredFacets,
          outerFacets,
          filteredMobileFacets,
          outerMobileFacets,
          onlyLastChilds,
          notLastUniqueIds,
          facetLocalChipsState,
          flatFacetsList,
          facetsChipsState,
          facetsState
        }
        ) => {
            this.enableFiltersOnIncomingResponse();
            this.facetsDataState = {...this.facetsDataState, ...{facets, facetsChipsState, facetsState, filteredMobileFacets, outerMobileFacets, flatFacetsList, totalHits}};
            if(this.facetsService.currentContext === FilterChangeContext.resetAll) {
              this.facetsDataState.facetLocalChipsState = facetLocalChipsState;
            }
            if(
              this.facetsDataState?.facetsState
          && this.facetsDataState.facetsState.length === 0
          && !(this.mobileFiltersElement as HTMLElement).classList.contains('fixed')
            ) {
              this.onClearAllViewChange();
            }
        });
  }

  private onClearAndClose(params: { onlyCollapse: boolean, collapseState: boolean }): void {
      this.facetsDataState.facetsState.length > 0
        ? (!params.onlyCollapse && !params.collapseState) ? this.onClearAllViewChange() : this.onClearAllViewChangeWithSelected()
        : this.onClearAllViewChange();

      if(!params.onlyCollapse && this.facetsDataState.facetsState.length > 0) {
        this.facetsService.skipWatchOnCount = false;
        this.facetsService.setFacetsRequestState([]);
        this.facetsService.setFacetsState([]);
        this.facetsService.setFacets(null);
        this.facetsService.setFacetsChipsState([]);
        this.mobileFiltersElement.clearSelected();
        this.countResults();
      }

      if(!params.collapseState) {
        this.mobileFilteringClose.emit({
          tabName: this.tabName,
          siteSearchKeyword: this.coseRequestService.context.query
        });
      }
  }

  private clearNoQueryFilters() {
      const previewsClone = cloneObjectWithNoReference(this.facetsDataState.previewsStateData);
      const toRemove = [];
      const uniqueIds = cloneObjectWithNoReference(previewsClone.facetsChipsState).map(f => f.uniqueId);
      cloneObjectWithNoReference(this.facetsDataState.facetsChipsState).forEach(fprev => {
          if(!uniqueIds.includes(fprev.uniqueId)) {
            toRemove.push(fprev);
          }
      });

      const {previewsStateData, ...rest} = previewsClone;
      const toRemoveIds = toRemove.map(filterNode => {
          return filterNode.uniqueId;
      });

      const clearChips = (chips, ids) => {
          return chips.map(chip => {
              if(!ids.includes(chip.uniqueId)) {
                return chip;
              }
          });
      };

      const clearChipsState = clearChips(previewsClone.facetsChipsState, toRemoveIds);
      previewsClone.facetsChipsState = clearChipsState;
      rest.facetsChipsState = clearChipsState;
      previewsClone.previewsStateData = {...this.facetsDataState.previewsStateData, ...rest};
      this.facetsDataState = cloneObjectWithNoReference(previewsClone);

      return clearChipsState;
  }

  private onEscape() {
      const clearChipsState = this.clearNoQueryFilters();
      this.facetsService.setFacetsChipsState(clearChipsState);
  }

  private onReset() {
      this.facetsService.currentContext = FilterChangeContext.resetAll;
      this.countResults();
      this.mobileFiltersElement.clearSelected();
      this.coseRequestService.onQueryContext.pipe(
        takeUntil(this.unsubscriber$),
        first()
      ).subscribe(value => {
          if(value === QueryContext.mobileFilteringShowResults) {
            this.reset.emit();
            this.eventEmitOnResetButtonClick(value);
            this.clearAllViewChange();
            this.setFixedView(false);
            this.setCollapse(false);
            this.coseRequestService.setOnQueryContext(QueryContext.ignored);
          } else {
            this.facetsService.clearMobileStates();
          }
      });
  }

  private observeProductsCardClientPosition() {
      const cardWrappers = [...this.coseRequestService.context.el.shadowRoot.querySelectorAll('.results-container>.result-card__wrapper')];

      if(cardWrappers.length >= this.positionCardNumberToHideMF) {
        this.cardPositionIntervalWatch = setInterval(() => {
            if(cardWrappers[this.positionCardNumberToHideMF - 1].getBoundingClientRect().top <= 0 && !this.mobileFiltersElement.classList.contains('was-expanded')) {
              this.mobileFiltersElement.setCanHide(true);
            } else {
              this.mobileFiltersElement.setCanHide(false);
            }
        }, 100);
      }
  }

  private onClearAllViewChange(): void {
      this.bodyScrollEnable();
      if(this.cardPositionIntervalWatch) {
        clearInterval(this.cardPositionIntervalWatch);
      }
      window.scrollTo(0, 0);
      this.showHideHeader(true);
      this.showHideResultCount(false);
      this.mobileFiltersElement.classList.remove('expand-width');
  }

  private onClearAllViewChangeWithSelected(): void {
      this.bodyScrollEnable();
      if(this.cardPositionIntervalWatch) {
        clearInterval(this.cardPositionIntervalWatch);
      }
      this.observeProductsCardClientPosition();
      this.mobileFiltersElement.classList.remove('was-expanded');
      this.mobileFiltersElement.classList.remove('expand-width');
      this.scrollYOnQuery = (this.tabsNavigatianHeader.offsetHeight + this.autocorrectComponent.offsetHeight) - this.headerMenuHeight;
      window.scrollTo(0, this.scrollYOnQuery);
      this.showHideHeader();
      this.showHideResultCount(true);
  }

  private showHideHeader(show = false): void {
      this.headerElement.style.display = show ? this.initialHeaderDisplayCss === '' ? 'block' : this.initialHeaderDisplayCss : 'none';
  }

  private showHideResultCount(show: boolean): void {
      if(show) {
        this.countResultsElement.classList.remove('hidden');
      } else {
        this.countResultsElement.classList.add('hidden');
      }
  }

  private bodyScrollDisable(): void {
      document.body.style.height = '100%';
      document.body.style.overflow = 'hidden';
  }

  private bodyScrollEnable(): void {
      document.body.style.height = 'inherit';
      document.body.style.overflow = 'visible';
  }
}
